import { memo } from 'react';

import { cn } from '../../../../utils/cn';
import { CatPart } from '../cat.const';

interface BucketButtonProps {
  /** Zero-based bucket index (0–99). Displayed as `index + 1`. */
  index: number;
  /** Whether this bucket is part of the committed selection. */
  isActive: boolean;
  /**
   * Whether this bucket is being previewed as part of an in-progress
   * drag or click-range selection.
   */
  isPreview: boolean;
  /** Whether this is the anchor bucket of the current click-range selection. */
  isStart: boolean;
  /**
   * Whether the in-progress selection would deselect this bucket
   * (i.e. all previewed buckets are already active).
   * Only relevant when `isPreview` is `true`.
   */
  willDeselect: boolean;
  /** Whether the entire input is disabled. */
  disabled: boolean;
  /** Whether the quadrant this bucket belongs to is locked against changes. */
  isLocked: boolean;
  /** Called with `index` when the primary mouse button is pressed on this cell. */
  onMouseDown: (index: number) => void;
  /** Called with `index` when the pointer enters this cell. */
  onMouseEnter: (index: number) => void;
  /** Cat-mode classification for this bucket, when the cat pattern is active. */
  catPart?: CatPart;
}

/**
 * A single cell in the 10×10 bucket selection grid.
 *
 * Wrapped in `memo` because the parent re-renders on every pointer event
 * (hover, drag) and there are 100 instances — bailing out of unchanged cells
 * is critical for smooth interaction.
 */
export const BucketButton = memo(function BucketButton({
  index,
  isActive,
  isPreview,
  isStart,
  willDeselect,
  disabled,
  isLocked,
  catPart,
  onMouseDown,
  onMouseEnter,
}: BucketButtonProps) {
  return (
    <button
      title={`Bucket ${index + 1}`}
      aria-label={`Bucket ${index + 1}`}
      className={cn(
        'size-6 rounded-full text-[10px] transition-colors duration-200 ease-in-out select-none',
        {
          'ring-2 ring-primary ring-offset-1': isStart,
          'cursor-not-allowed opacity-60': isLocked,
          'cursor-not-allowed opacity-50': !isLocked && disabled,
          'bg-destructive text-destructive-foreground': isPreview && willDeselect,
          'bg-primary/50 text-primary-foreground': isPreview && !willDeselect,
          'bg-orange-400 text-white': !isPreview && isActive && catPart === 'ears',
          'bg-orange-400/60 text-orange-950': !isPreview && isActive && catPart === 'head-body',
          'bg-orange-400/90 text-white': !isPreview && isActive && catPart === 'paws',
          'bg-zinc-500 text-white': !isPreview && isActive && catPart === 'tail',
          'bg-primary text-primary-foreground': !isPreview && isActive && catPart === undefined,
          'bg-muted text-muted-foreground hover:bg-muted/50': !isPreview && !isActive,
        },
      )}
      disabled={disabled || isLocked}
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onMouseDown(index);
      }}
      onMouseEnter={() => onMouseEnter(index)}
    >
      {index + 1}
    </button>
  );
});
